import { Highlight } from '@curology/ui-components-web/react/Highlight';
import { Typography } from '@curology/ui-components-web/react/Typography';
import Image from 'next/image';
import React from 'react';

import { type Card } from '@/types/productSelectionAndCheckout/types';

import { IngredientsAccordion } from '../IngredientsAccordion';
import { ModalImage } from '../ModalImage';
import { ModalPricingVolumeAndProductOptionSelector } from '../ModalPricingVolumeAndProductOptionSelector';

export const HydroTretModalContent = ({ modalCard }: { modalCard: Card }) => {
  const { modal } = modalCard;

  return (
    <>
      <ModalImage modal={modal} />
      <Typography className="cur-mt-4" size="h2">
        HydroTret<sup>Rx</sup>
      </Typography>
      <Typography className="cur-mt-4" weight="bold">
        Hydrating Tretinoin-Powered Treatment
      </Typography>
      <ModalPricingVolumeAndProductOptionSelector
        className="cur-mt-1"
        modalCard={modalCard}
      />
      {modalCard.recommendationText && (
        <Highlight className="cur-mt-4" state="success">
          <div
            dangerouslySetInnerHTML={{
              __html: modalCard.recommendationText.modalCallout,
            }}
          ></div>
        </Highlight>
      )}
      <Typography className="cur-mt-4" size="h3">
        Why it&apos;s special
      </Typography>
      <Typography className="cur-mt-4">
        Lock in a healthy glow with HydroTret<sup>Rx</sup>, designed to be our
        most hydrating tretinoin formula to keep your skin looking its
        healthiest.
      </Typography>
      <Typography className="cur-mt-4">
        Made with a personalized blend of powerful tretinoin and hydrating
        dexpanthenol (also known as provitamin B5), this formula helps reinforce
        your skin barrier to keep essential hydration in while helping to boost
        collagen production for smoother, healthy-looking skin.
      </Typography>
      <Typography className="cur-mt-4">
        Beyond hydration benefits, its active ingredients help promote clearer
        skin by visibly fading dark spots and preventing future breakouts—so you
        can stay glowing today and tomorrow.
      </Typography>
      <Typography className="cur-mt-4">
        Your provider will review your answers and photos, and pick a few of the
        following active ingredients for your skin&apos;s unique needs.* We also
        offer a tretinoin-free HydroTret<sup>Rx</sup> if that is a better option
        for your unique skin.
      </Typography>
      <Typography className="cur-mt-4">
        *Product and ingredients subject to medical consultation. Do not use
        tretinoin if you are pregnant, trying to conceive, or breastfeeding.
      </Typography>
      <ul className="cur-flex cur-items-center cur-text-center cur-mt-4">
        <li className="cur-flex-1">
          <div className="cur-pr-2">
            <Image
              alt="Dermatologist badge"
              className="cur-h-6 cur-inline-block"
              height={24}
              src="https://images.ctfassets.net/8pzh4g8cs30t/35Tu0N4YJs6ACdp3K8oQo8/50fa0bc9b15e325647766218617dfa9c/barrier-balm-dermatologist-badge.png"
              width={24.5}
            />
            <Typography className="cur-mt-2" size="meta">
              Dermatologist
            </Typography>
            <Typography size="meta">Founded. Developed. Tested.</Typography>
          </div>
        </li>
      </ul>
      <hr className="cur-bg-surface-disabled cur-border-0 cur-h-[1px] cur-mt-4" />
      <Typography className="cur-mt-4" size="h3">
        How to use it
      </Typography>
      <Typography className="cur-mt-4">
        Our licensed dermatology providers generally advise: After cleansing,
        apply a thin layer of HydroTret<sup>Rx</sup> to your entire face and
        neck before bed. Leave on overnight. As always, follow your
        provider&apos;s instructions for your unique skin.
      </Typography>
      <Typography className="cur-mt-4" size="h3">
        Active ingredients
      </Typography>
      <IngredientsAccordion
        body="Helps smooth texture and fine lines while brightening skin"
        title="Tretinoin"
      />
      <IngredientsAccordion
        body="Attracts and holds moisture for softer, smoother skin"
        title="Dexpanthenol"
      />
      <IngredientsAccordion
        body="Fights free radicals, fades dark spots, and improves moisture
        barrier function"
        title="Niacinamide"
      />
      <IngredientsAccordion
        body="Gently exfoliates and reduces excess pigment to help smooth and
        brighten skin"
        title="Azelaic acid"
      />
      <Typography className="cur-mt-4" size="h3">
        Inactive ingredients
      </Typography>
      <Typography className="cur-mt-4">
        Aloe Barbadensis Leaf Juice, C13-C14 Isoparaffin, Caprylic/Capric
        Triglyceride, Deionized Water, Laureth-7, Phenoxyethanol,
        Polyacrylamide, Tocopheryl Acetate, Triethylene Glycol
      </Typography>
      <Typography className="cur-mt-4">
        Formulas containing tretinoin also contain the antioxidant butylated
        hydroxytoluene (BHT), and formulas containing dexpanthenol also contain
        propylene glycol.
      </Typography>
    </>
  );
};
